import Page from "../../components/Page";
import Card from "../../components/Card";
import CardHeading from "../../components/CardHeading";
import {
  Alert,
  AlertTitle,
  Chip,
  Grid,
  IconButton,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchInvoiceById, submitInvoice } from "../../store/actions/invoice";
import { Link, useNavigate, useParams } from "react-router-dom";
import Table from "@mui/material/Table";
import { decodeId, formatDate, formatNumber } from "../../utils/helper";
import { makeStyles } from "@mui/styles";
import { Add, Remove } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  claimChipColor,
  claimStatusMapping,
  claimStatusStyles,
  CLAIM_STATUS_CREATED,
  CLAIM_STATUS_REJECTED,
  CLAIM_STATUS_REWORK,
  invoiceBadgeColor,
  invoiceStatus,
} from "../../utils/statusMap";

import ConfirmationDialog from "../../components/ConfirmationDialog";
import Loader from "../../components/Loader";
import CommonSnackbar from "../../reusableComponents/components/generics/CommonSnackbar";
import { LoadingButton } from "../../reusableComponents/components/generics/LoadingButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { CLAIM_FORM_BY_ID } from "../../utils/constant";
import { generateInvoice } from "../../action";
import { useConvention } from "../../hooks/useConvention";

const useStyles = makeStyles((theme) => ({
  servicesHeader: {
    backgroundColor: "#E5EAEA",
  },
  tableCardContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  detailContainer: {
    padding: "1rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.8rem",
  },
  flex: {
    display: "flex",
    gap: "1rem",
    alignItems: "center",
  },
  numberLabel: {
    fontSize: "1.5rem",
    fontWeight: "bold",
  },
  numberValue: {
    fontSize: "1.1rem",
    fontWeight: "bold",
    color: "#000",
  },
}));

const StyledAlertTitle = styled(AlertTitle)(({}) => ({
  fontWeight: "bold",
  color: "red",
}));

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: undefined,
    severity: undefined,
  });

  const { t } = useTranslation();

  const { isConventionnementActive } = useConvention();

  const closeSnackbar = useCallback(() => {
    setSnackbarState((state) => ({
      ...state,
      message: undefined,
      severity: undefined,
      open: false,
    }));
  }, []);

  const loadData = useCallback(async () => {
    try {
      const { data } = await fetchInvoiceById(id);
      console.log("fetched data", data);
      const invoice = data.claimInvoice.edges[0].node;
      console.log("=== invoice", invoice);
      setInvoice(invoice);
    } catch (e) {
      console.error(e);
    }
  }, [id, setInvoice]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const items = useMemo(() => {
    if (!invoice) return [];

    return invoice.claimInvoice.edges.map((e) => e.node);
  }, [invoice]);

  const canSubmit = useMemo(() => {
    if (!invoice) {
      return false;
    }

    return invoice.status === "CREATED" || invoice.status === "REJECTED";
  }, [invoice]);

  const handleSubmit = useCallback(async () => {
    const claimUUIDs = items.map((claim) => claim.uuid);
    const invoiceId = decodeId(invoice.id);

    setDialogOpen(false);
    setSubmitting(true);

    try {
      const response = await submitInvoice(invoiceId, claimUUIDs);
      console.log("response.payload", response);

      setSnackbarState({
        open: true,
        message: "La facture à bien été soumis!",
        severity: "success",
      });
      loadData();
    } catch (e) {
      console.error(e);
      setSnackbarState({
        open: true,
        message: "Une erreur s'est produite, veuillez réessayer!",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
    console.log("claimUUIDs", claimUUIDs);
  }, [invoice, items]);

  const hasRejectedClaim = items?.find(
    (item) => item.status == 1 && item.isFullRejected === true
  );
  const hasContentieuxClaim = items?.find(
    (item) => item.status == 1 && item.isFullRejected === false
  );
  return (
    <Page
      title="Détail de la demande de paiement"
      showNavigation
      actions={
        <>
          {canSubmit && isConventionnementActive && (
            <LoadingButton
              onClick={() => setDialogOpen(true)}
              variant="contained"
              loading={isSubmitting}
            >
              Soumettre
            </LoadingButton>
          )}
        </>
      }
    >
      <Grid container>
        <Grid item xs={12}>
          {hasRejectedClaim && (
            <Alert
              severity="error"
              sx={{
                border: "1px solid red",
                marginInline: "1rem",
                backgroundColor: "#ffe6e6", // light red background
                color: "red",
              }}
            >
              <StyledAlertTitle>
                {
                  "Une ou plusieurs feuille de soins de cette demande ont été rejetées, veuillez le retirer avant de resoumettre cette demande, les causes de rejet sont disponible au niveau de chaque feuille rejetés. le statuts  te permettrons d’identifier les feuilles rejetées."
                }
              </StyledAlertTitle>
            </Alert>
          )}
          {hasContentieuxClaim && !hasRejectedClaim && (
            <Alert
              severity="warning"
              sx={{
                border: "1px solid orange",
                marginInline: "1rem",
              }}
            >
              <StyledAlertTitle>
                {
                  "Une ou plusieurs feuille de soins de cette demande en contentieux, veuillez le mettre à jour avant de resoumettre cette demande, les causes de rejet sont disponible au niveau de chaque feuille rejetés. le statuts  te permettrons d’identifier les feuilles rejetées."
                }
              </StyledAlertTitle>
            </Alert>
          )}
          <Card id="my-item">
            <PageContent invoice={invoice} items={items} loadData={loadData} />
          </Card>
        </Grid>
      </Grid>

      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleSubmit}
        title="Confirmation"
        confirmText="Oui, soumettre"
        cancelText="Annuler"
        message={`Are you sure you want to submit the following invoice? 
                  \nInvoice No. - ${invoice && invoice.invoiceNumber} 
                  `}
      />

      <CommonSnackbar
        severity={snackbarState.severity}
        open={snackbarState.open}
        onClose={closeSnackbar}
        message={snackbarState.message}
      />
    </Page>
  );
}

function PageContent({ invoice, items, loadData }) {
  const classes = useStyles();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [snackBarState, setSnackbarState] = useState({
    isOpen: false,
    message: undefined,
    severity: undefined,
  });

  const navigate = useNavigate();

  const closeSnackbar = useCallback(() => {
    setSnackbarState({
      isOpen: false,
      message: undefined,
      severity: undefined,
    });
  }, [setSnackbarState]);

  if (!invoice) {
    return <Loader />;
  }

  const handleSelect = async (row) => {
    setSelectedRow(row);
    setDialogOpen(true);
  };

  const goBack = () => {
    navigate(-1);
  };

  const handleDelete = async () => {
    const claimUUIDs = items
      ?.filter((item) => item.uuid !== selectedRow?.uuid)
      .map((item) => item.uuid);
    const invoiceId = decodeId(invoice.id);

    setSubmitting(true);
    setDialogOpen(false);
    try {
      const response = await generateInvoice(
        claimUUIDs,
        invoiceId,
        invoice.note
      );
      console.log("response.payload", response);

      setSnackbarState({
        open: true,
        message: "La facture à bien été modifié!",
        severity: "success",
      });

      if (claimUUIDs.length > 0) {
        loadData();
      } else {
        goBack();
      }
    } catch (e) {
      console.error(e);
      setSnackbarState({
        open: true,
        message: "Une erreur s'est produite, veuillez réessayer!",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CardHeading
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <Typography variant="h6">
            Facture N° {invoice.invoiceNumber}
          </Typography>
          <Typography>Soumis le : {formatDate(invoice.dateCreated)}</Typography>
        </Grid>

        <Grid item>
          <div className={classes.flex}>
            <span>Status</span>{" "}
            <Chip
              label={invoiceStatus(invoice.status)}
              color={invoiceBadgeColor(invoice.status)}
            />
          </div>
        </Grid>
      </CardHeading>

      <Table sx={{ minWidth: 250 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell style={{ width: "50px" }}></TableCell>
            <TableCell>N° de la demande</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Code CAMU</TableCell>
            <TableCell>Bénéficiaire</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Total</TableCell>
            <TableCell align="right">Montant Non couvert</TableCell>
            <TableCell align="right">Montant couvert</TableCell>
            <TableCell style={{ width: "50px" }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row) => (
            <TableItem onDelete={handleSelect} key={row.id} row={row} />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={7} rowSpan={3}>
              <Typography>{invoice.note}</Typography>
            </TableCell>
            <TableCell>
              <Typography classes={classes.numberLabel}>Sous Total</Typography>
            </TableCell>
            <TableCell align="right">
              <span className={classes.numberValue}>
                {formatNumber(invoice.subTotal)}
              </span>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography>Total non couvert</Typography>
            </TableCell>
            <TableCell align="right">
              <span className={classes.numberValue}>
                {formatNumber(invoice.totalModerator)}
              </span>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography>Total couvert</Typography>
            </TableCell>
            <TableCell align="right">
              <span className={classes.numberValue}>
                {formatNumber(
                  parseFloat(invoice.totalAmount) -
                    parseFloat(invoice.totalModerator)
                )}
              </span>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleDelete}
        title="Confirmation"
        confirmText="Oui, retirer"
        cancelText="Annuler"
        message={`Voulez-vous vraiment retirer la feuille de soin ${selectedRow?.code} ?`}
      />

      <CommonSnackbar
        open={snackBarState.isOpen}
        onClose={closeSnackbar}
        message={snackBarState.message}
        severity={snackBarState.severity}
      />
    </>
  );
}

function TableItem({ row, onDelete }) {
  const classes = useStyles();
  const [isExpanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const { isConventionnementActive } = useConvention();

  const canUpdate = (status) => {
    if (!isConventionnementActive) return false;
    return (
      status === CLAIM_STATUS_REJECTED ||
      status === CLAIM_STATUS_CREATED ||
      status === CLAIM_STATUS_REWORK
    );
  };

  const isContentieux = (claim) => {
    return (
      claim.status === CLAIM_STATUS_REJECTED && claim.isFullRejected === false
    );
  };

  return (
    <>
      <TableRow>
        <TableCell>
          {isConventionnementActive && (
            <IconButton onClick={() => setExpanded((exp) => !exp)}>
              {isExpanded ? <Remove /> : <Add />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Link to={`${CLAIM_FORM_BY_ID}/${row.uuid}`} target="_blank">
            {row.code}
          </Link>
        </TableCell>
        <TableCell>{formatDate(row.dateClaimed, "dd/MM/yyyy")}</TableCell>
        <TableCell>{row.insuree.camuNumber}</TableCell>
        <TableCell>
          {row.insuree.lastName} {row.insuree.otherNames}
        </TableCell>
        <TableCell style={claimStatusStyles[row.status]}>
          <Chip
            color={claimChipColor(row.status)}
            label={claimStatusMapping(t)[isContentieux(row) ? 128 : row.status]}
          />
        </TableCell>
        <TableCell align="right">{formatNumber(row.claimed)}</TableCell>
        <TableCell align="right">{formatNumber(row.totalModerator)}</TableCell>
        <TableCell align="right">
          {formatNumber(
            parseFloat(row.claimed) - parseFloat(row.totalModerator)
          )}
        </TableCell>
        <TableCell>
          {canUpdate(row.status) && (
            <IconButton
              onClick={() => onDelete(row)}
              size="small"
              color="danger"
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow>
          <TableCell></TableCell>
          <TableCell colSpan={9}>
            <div className={classes.tableCardContainer}>
              {row.acts.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead className={classes.servicesHeader}>
                      <TableRow>
                        <TableCell style={{ width: "40%" }}>
                          Acte de soin
                        </TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Quantité</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell align="right">Montant non couvert</TableCell>
                        <TableCell align="right">Montant couvert</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.acts.map((service) => (
                        <TableRow key={service.id}>
                          <TableCell>{service?.act?.label}</TableCell>
                          <TableCell>{service?.act?.fullCode}</TableCell>
                          <TableCell>
                            {formatNumber(service.qtyProvided)}
                          </TableCell>
                          <TableCell>
                            {formatNumber(
                              parseInt(service.qtyProvided) *
                                parseFloat(service.priceAsked)
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(JSON.parse(service.jsonExt).tmValue)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(
                              JSON.parse(service.jsonExt).countedPrice
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}

              {row.claimProducts.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead className={classes.servicesHeader}>
                      <TableRow>
                        <TableCell style={{ width: "40%" }}>Produit</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Quantité</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell align="right">Montant non couvert</TableCell>
                        <TableCell align="right">Montant couvert</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.claimProducts.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.healthProduct.label}</TableCell>
                          <TableCell>{item.healthProduct.fullCode}</TableCell>
                          <TableCell>
                            {formatNumber(item.qtyProvided)}
                          </TableCell>
                          <TableCell>
                            {formatNumber(
                              parseFloat(item.qtyProvided) *
                                parseFloat(item.priceAsked)
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(JSON.parse(item.jsonExt).tmValue)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(
                              JSON.parse(item.jsonExt).countedPrice
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
